export type FormatNumberOptions = {
    decimalPlaces?: number;
    placeholder?: string;
    trimTrailingZeros?: boolean;
};

/**
 * Formats numeric values using truncation for the configured decimal precision.
 * Values are truncated (not rounded) and trailing zeros are removed by default.
 */
export const formatNumber = (value: unknown, options: FormatNumberOptions = {}): string => {
    const { decimalPlaces = 4, placeholder = '–', trimTrailingZeros = true } = options;

    if (value === null || value === undefined || value === '') {
        return placeholder;
    }

    const num = Number(value);
    if (Number.isNaN(num) || !Number.isFinite(num)) {
        return String(value);
    }

    if (decimalPlaces <= 0) {
        const integerOnly = Math.trunc(num);
        return integerOnly.toLocaleString('en-US');
    }

    const factor = 10 ** decimalPlaces;
    const truncatedScaled = Math.trunc(num * factor);
    const absScaled = Math.abs(truncatedScaled);
    const integerPart = Math.trunc(absScaled / factor);
    const decimalInt = absScaled % factor;
    const signPrefix = truncatedScaled < 0 ? '-' : '';
    const integerStr = integerPart.toLocaleString('en-US');

    if (decimalInt === 0) {
        return `${signPrefix}${integerStr}`;
    }

    let decimalStr = decimalInt.toString().padStart(decimalPlaces, '0');
    if (trimTrailingZeros) {
        decimalStr = decimalStr.replace(/0+$/, '');
    }

    if (!decimalStr) {
        return `${signPrefix}${integerStr}`;
    }

    return `${signPrefix}${integerStr}.${decimalStr}`;
};

const COMPACT_UNITS = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
];

/**
 * Formats numeric values with K/M/B/T suffixes (e.g. 1.2M, 3.4B).
 * Values below 1,000 use plain thousands-separator formatting.
 */
export const formatCompact = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return '–';
    }

    const num = Number(value);
    if (Number.isNaN(num) || !Number.isFinite(num)) {
        return String(value);
    }

    for (const { limit, suffix } of COMPACT_UNITS) {
        if (Math.abs(num) >= limit) {
            const compact = num / limit;
            const digits = Math.abs(compact) >= 100 ? 0 : 1;
            return `${compact.toFixed(digits).replace(/\.0$/, '')}${suffix}`;
        }
    }

    return num.toLocaleString('en-US');
};
